import type { TicketChannelV3Condition as TicketChannelV3ConditionType } from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  MESSAGE_CHANNELS_V3,
  type MessageChannelV3,
} from "@redotech/redo-model/advanced-flow/rules-v3/types";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const CHANNEL_DISPLAY_NAMES: Record<MessageChannelV3, string> = {
  gmail: "Gmail",
  outlook: "Outlook",
  redoChat: "Chat",
  facebook: "Facebook",
  facebookComments: "Facebook Comments",
  instagram: "Instagram",
  instagramComments: "Instagram Comments",
  sms: "SMS",
  voice: "Voice",
  attentive: "Attentive",
  postscript: "Postscript",
  tiktok: "TikTok",
  contactForm: "Contact Form",
};

const channelItems: RedoListItem<MessageChannelV3>[] = MESSAGE_CHANNELS_V3.map(
  (channel) => ({
    id: channel,
    type: "text" as const,
    text: CHANNEL_DISPLAY_NAMES[channel],
    value: channel,
  }),
);

export const TicketChannelV3Condition = memo(function TicketChannelV3Condition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TicketChannelV3ConditionType;
  onChange: (updated: TicketChannelV3ConditionType) => void;
  /**
   * When omitted, all fields are editable. When provided, only fields
   * in the set are editable; others render read-only. Used by the
   * default-rule edit page.
   */
  editableFields?: ReadonlySet<string>;
}) {
  const channelsEditable =
    editableFields === undefined || editableFields.has("channels");

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<MessageChannelV3>[] = useMemo(() => {
    return (condition.channels || []).map((channel) => ({
      id: channel,
      type: "text" as const,
      text: CHANNEL_DISPLAY_NAMES[channel],
      value: channel,
    }));
  }, [condition.channels]);

  const displayItem: RedoListItem<MessageChannelV3> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: `${selectedItems.length} channels selected`,
        value: "gmail" as MessageChannelV3,
      };
    }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        Channel
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!channelsEditable}
        isDropdownOpen={dropdownOpen}
        onClick={() => channelsEditable && setDropdownOpen(!dropdownOpen)}
        placeholder="Select channels"
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={channelItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!channelsEditable) {
            return;
          }
          onChange({ ...condition, channels: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
